import { createElement, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { marked } from "marked";
import type { CitationContentPart, Message } from "../../api/types.js";
import { isSafeHref } from "../shared/safeHref.js";

const citationPlaceholder = (prefix: string, index: number) => `${prefix}${index}@@`;

function publicText(text: string) {
  return text
    .replaceAll(/Pi\s*Agent/gi, "Truth-Seeker")
    .replaceAll(/\bPi\b/g, "Truth-Seeker")
    .replaceAll(/本地后端/g, "科研助理");
}

const allowedMarkdownTags = new Set(["A", "BLOCKQUOTE", "BR", "CODE", "EM", "H1", "H2", "H3", "H4", "H5", "H6", "HR", "LI", "OL", "P", "PRE", "STRONG", "TABLE", "TBODY", "TD", "TH", "THEAD", "TR", "UL"]);

type SafeMarkdownNode =
  | { kind: "text"; key: string; text: string }
  | { kind: "citation"; key: string; localIndex: number; occurrence: number }
  | { kind: "element"; key: string; tag: string; props: Record<string, string>; children: SafeMarkdownNode[] };

function escapeRawHtml(markdown: string) {
  return markdown.replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[char] || char);
}

function sanitizeMarkdownTree(root: DocumentFragment) {
  for (const element of Array.from(root.querySelectorAll("*"))) {
    if (!allowedMarkdownTags.has(element.tagName)) {
      element.replaceWith(document.createTextNode(element.textContent || ""));
      continue;
    }
    for (const attribute of Array.from(element.attributes)) {
      const tag = element.tagName;
      const allowed = tag === "A" ? ["href", "title", "target", "rel"] : [];
      if (!allowed.includes(attribute.name)) element.removeAttribute(attribute.name);
    }
    if (element.tagName === "A") {
      const href = element.getAttribute("href") || "";
      if (!isSafeHref(href)) {
        element.removeAttribute("href");
      } else {
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noreferrer");
      }
    }
  }
}

function citationPartFromMapEntry(entry: any): Extract<CitationContentPart, { type: "citation" }> | null {
  const localIndex = Number(entry?.localIndex);
  if (!Number.isInteger(localIndex) || localIndex <= 0 || !entry?.poolId) return null;
  return {
    type: "citation",
    text: `[${localIndex}]`,
    localIndex,
    poolId: entry.poolId,
    sourceType: entry.sourceType,
    referenceItemId: entry.referenceItemId || null,
    webSourceId: entry.webSourceId || null,
    evidenceSnippetId: entry.evidenceSnippetId || null,
    citationMapEntry: entry
  };
}

function contentPartsFromCitationMap(message: Message): CitationContentPart[] {
  const content = message.content || "";
  const entries = Array.isArray(message.meta?.citationMap) ? message.meta.citationMap : [];
  const reliableSpans = entries
    .map((entry: any) => {
      const localIndex = Number(entry?.localIndex);
      const finalStart = Number(entry?.finalStart);
      const finalEnd = Number(entry?.finalEnd);
      const expected = `[${localIndex}]`;
      if (!Number.isInteger(localIndex) || !Number.isInteger(finalStart) || !Number.isInteger(finalEnd)) return null;
      if (finalStart < 0 || finalEnd <= finalStart || finalEnd > content.length) return null;
      if (content.slice(finalStart, finalEnd) !== expected) return null;
      const part = citationPartFromMapEntry(entry);
      return part ? { finalStart, finalEnd, part } : null;
    })
    .filter(Boolean)
    .sort((a: any, b: any) => a.finalStart - b.finalStart);
  const summary = message.meta?.citationGate?.rawCitationSummary;
  const canExpandLegacyRepeatedMarkers = Boolean(
    summary
      && summary.bracketCitationCount === 0
      && summary.circledCitationCount === 0
      && !summary.bibliographyWithoutTokens
  );
  const spans = [...reliableSpans] as Array<{ finalStart: number; finalEnd: number; part: CitationContentPart }>;
  if (canExpandLegacyRepeatedMarkers) {
    for (const entry of entries) {
      const part = citationPartFromMapEntry(entry);
      if (!part) continue;
      const marker = `[${part.localIndex}]`;
      let start = content.indexOf(marker);
      while (start >= 0) {
        const finalEnd = start + marker.length;
        const overlaps = spans.some((span) => start < span.finalEnd && finalEnd > span.finalStart);
        if (!overlaps) spans.push({ finalStart: start, finalEnd, part });
        start = content.indexOf(marker, finalEnd);
      }
    }
    spans.sort((a, b) => a.finalStart - b.finalStart);
  }
  const parts: CitationContentPart[] = [];
  let cursor = 0;
  for (const span of spans) {
    if (span.finalStart < cursor) continue;
    if (span.finalStart > cursor) parts.push({ type: "text", text: content.slice(cursor, span.finalStart) });
    parts.push(span.part);
    cursor = span.finalEnd;
  }
  if (cursor < content.length) parts.push({ type: "text", text: content.slice(cursor) });
  return parts.length ? parts : [{ type: "text", text: content }];
}

function contentPartsForMessage(message: Message): CitationContentPart[] {
  const parts = Array.isArray(message.meta?.contentParts) ? message.meta.contentParts : [];
  if (parts.length) {
    const normalized = parts
      .map((part: any) => {
        if (part?.type === "citation") return citationPartFromMapEntry(part.citationMapEntry || part) || null;
        if (part?.type === "text" && typeof part.text === "string") return { type: "text", text: part.text };
        return null;
      })
      .filter(Boolean) as CitationContentPart[];
    if (normalized.length) return normalized;
  }
  return contentPartsFromCitationMap(message);
}

function markdownFromParts(parts: CitationContentPart[]) {
  const citations: Array<Extract<CitationContentPart, { type: "citation" }>> = [];
  const rawText = parts.map((part) => part.text || "").join("");
  let nonce = 0;
  let placeholderPrefix = "@@TS_CITATION_0_";
  while (rawText.includes(placeholderPrefix)) {
    nonce += 1;
    placeholderPrefix = `@@TS_CITATION_${nonce}_`;
  }
  let markdown = "";
  for (const part of parts) {
    if (part.type === "citation") {
      markdown += citationPlaceholder(placeholderPrefix, citations.length);
      citations.push(part);
    } else {
      markdown += part.text || "";
    }
  }
  return { markdown, citations, placeholderPrefix };
}

function injectCitationButtons(
  root: DocumentFragment,
  citations: Array<Extract<CitationContentPart, { type: "citation" }>>,
  placeholderPrefix: string,
  occurrenceOffset = 0,
  citationScope = ""
) {
  if (!citations.length) return;
  const placeholders = citations.map((_, index) => citationPlaceholder(placeholderPrefix, index));
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const text = node.textContent || "";
      return placeholders.some((placeholder) => text.includes(placeholder)) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  for (const node of nodes) {
    const fragment = document.createDocumentFragment();
    const text = node.textContent || "";
    let cursor = 0;
    const matches = placeholders
      .flatMap((placeholder, index) => {
        const found: Array<{ index: number; placeholder: string; citationIndex: number }> = [];
        let start = text.indexOf(placeholder);
        while (start >= 0) {
          found.push({ index: start, placeholder, citationIndex: index });
          start = text.indexOf(placeholder, start + placeholder.length);
        }
        return found;
      })
      .sort((a, b) => a.index - b.index);
    for (const match of matches) {
      const citation = citations[match.citationIndex];
      if (!citation || match.index < cursor) continue;
      fragment.append(document.createTextNode(text.slice(cursor, match.index)));
      if (node.parentElement?.closest("a,button")) {
        fragment.append(document.createTextNode(citation.text));
      } else {
        const button = document.createElement("button");
        button.className = "citation-chip";
        button.dataset.citationIndex = String(citation.localIndex);
        button.dataset.citationOccurrence = String(occurrenceOffset + match.citationIndex);
        button.dataset.citationIdentity = [
          citationScope,
          citation.poolId,
          citation.referenceItemId || "",
          citation.evidenceSnippetId || ""
        ].join(":");
        button.type = "button";
        button.textContent = String(citation.localIndex);
        fragment.append(button);
      }
      cursor = match.index + match.placeholder.length;
    }
    fragment.append(document.createTextNode(text.slice(cursor)));
    node.replaceWith(fragment);
  }
}

function safeElementProps(element: HTMLElement) {
  if (element.tagName !== "A") return {};
  const props: Record<string, string> = {};
  for (const name of ["href", "title", "target", "rel"]) {
    const value = element.getAttribute(name);
    if (value !== null) props[name] = value;
  }
  return props;
}

function safeMarkdownNodes(parent: ParentNode, path = "root"): SafeMarkdownNode[] {
  return Array.from(parent.childNodes).flatMap<SafeMarkdownNode>((node, index): SafeMarkdownNode[] => {
    const key = `${path}:${index}`;
    if (node.nodeType === Node.TEXT_NODE) {
      return [{ kind: "text", key, text: node.textContent || "" } satisfies SafeMarkdownNode];
    }
    if (!(node instanceof HTMLElement)) return [];
    if (node.tagName === "BUTTON") {
      const localIndex = Number(node.dataset.citationIndex);
      const occurrence = Number(node.dataset.citationOccurrence);
      const identity = node.dataset.citationIdentity;
      if (!Number.isInteger(localIndex) || localIndex <= 0 || !Number.isInteger(occurrence) || occurrence < 0 || !identity) return [];
      return [{ kind: "citation", key: `citation:${occurrence}:${identity}`, localIndex, occurrence } satisfies SafeMarkdownNode];
    }
    if (!allowedMarkdownTags.has(node.tagName)) return [];
    return [{
      kind: "element",
      key: `${key}:${node.tagName}`,
      tag: node.tagName.toLowerCase(),
      props: safeElementProps(node),
      children: safeMarkdownNodes(node, key)
    } satisfies SafeMarkdownNode];
  });
}

type CitationNode = Extract<SafeMarkdownNode, { kind: "citation" }>;

function citationNodesIn(nodes: SafeMarkdownNode[], citations: CitationNode[] = []) {
  for (const node of nodes) {
    if (node.kind === "citation") citations.push(node);
    if (node.kind === "element") citationNodesIn(node.children, citations);
  }
  return citations;
}

function CitationSlot({ host, parkingRef }: { host: HTMLSpanElement; parkingRef: RefObject<HTMLSpanElement | null> }) {
  const slotRef = useRef<HTMLSpanElement | null>(null);
  const attachHost = useCallback((slot: HTMLSpanElement | null) => {
    const previousSlot = slotRef.current;
    slotRef.current = slot;
    if (slot) {
      slot.append(host);
    } else if (previousSlot?.contains(host)) {
      parkingRef.current?.append(host);
    }
  }, [host, parkingRef]);
  return createElement("span", { ref: attachHost, "data-citation-slot": "" });
}

function StableCitationButton({
  citation,
  onMouseDown
}: {
  citation: CitationNode;
  onMouseDown: (event: ReactMouseEvent<HTMLButtonElement>, citationKey: string) => void;
}) {
  return (
    <button
      className="citation-chip"
      data-citation-key={citation.key}
      data-citation-index={citation.localIndex}
      data-citation-occurrence={citation.occurrence}
      onMouseDown={(event) => onMouseDown(event, citation.key)}
      type="button"
    >
      {citation.localIndex}
    </button>
  );
}

function renderMarkdownNode(
  node: SafeMarkdownNode,
  citationHosts: Map<string, HTMLSpanElement>,
  parkingRef: RefObject<HTMLSpanElement | null>
): ReactNode {
  if (node.kind === "text") return createElement(Fragment, { key: node.key }, node.text);
  if (node.kind === "citation") {
    const host = citationHosts.get(node.key);
    return host ? createElement(CitationSlot, { key: node.key, host, parkingRef }) : null;
  }
  return createElement(
    node.tag,
    { ...node.props, key: node.key },
    ...node.children.map((child) => renderMarkdownNode(child, citationHosts, parkingRef))
  );
}

function renderSafeMarkdown(message: Message) {
  if (typeof document === "undefined") return [];
  const { markdown, citations, placeholderPrefix } = markdownFromParts(contentPartsForMessage(message));
  const template = document.createElement("template");
  template.innerHTML = marked.parse(escapeRawHtml(publicText(markdown))).toString();
  sanitizeMarkdownTree(template.content);
  injectCitationButtons(template.content, citations, placeholderPrefix, 0, String(message.meta?.activeAssistantSegmentId || ""));
  return safeMarkdownNodes(template.content);
}

export function MarkdownAnswer({ message, onCitation }: { message: Message; onCitation: (index: number, message: Message) => void }) {
  const citationHostsRef = useRef(new Map<string, HTMLSpanElement>());
  const markdownRootRef = useRef<HTMLDivElement | null>(null);
  const parkingRef = useRef<HTMLSpanElement | null>(null);
  const mouseGestureRef = useRef<{ citationKey: string; x: number; y: number } | null>(null);
  const nodes = useMemo(() => renderSafeMarkdown(message), [
    message.content,
    message.meta?.contentParts,
    message.meta?.citationMap,
    message.meta?.citationGate
  ]);
  const citations = useMemo(() => citationNodesIn(nodes), [nodes]);
  const activeCitationKeys = useMemo(() => new Set(citations.map((citation) => citation.key)), [citations]);
  for (const citation of citations) {
    if (citationHostsRef.current.has(citation.key)) continue;
    const host = document.createElement("span");
    host.style.display = "contents";
    citationHostsRef.current.set(citation.key, host);
  }
  useLayoutEffect(() => {
    if (mouseGestureRef.current && !activeCitationKeys.has(mouseGestureRef.current.citationKey)) {
      mouseGestureRef.current = null;
    }
    for (const [key, host] of citationHostsRef.current) {
      if (activeCitationKeys.has(key)) continue;
      host.remove();
      citationHostsRef.current.delete(key);
    }
  }, [activeCitationKeys]);
  const beginMouseGesture = useCallback((event: ReactMouseEvent<HTMLButtonElement>, citationKey: string) => {
    if (event.button !== 0) return;
    mouseGestureRef.current = { citationKey, x: event.clientX, y: event.clientY };
  }, []);
  useEffect(() => {
    const finishMouseGesture = (event: MouseEvent) => {
      const gesture = mouseGestureRef.current;
      mouseGestureRef.current = null;
      if (!gesture || event.button !== 0) return;
      const moved = Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y);
      if (moved > 12) return;
      const button = Array.from(markdownRootRef.current?.querySelectorAll<HTMLButtonElement>("[data-citation-key]") || [])
        .find((candidate) => candidate.dataset.citationKey === gesture.citationKey);
      if (!button) return;
      const releaseTarget = document.elementFromPoint(event.clientX, event.clientY);
      if (releaseTarget === button || button.contains(releaseTarget)) return;
      event.preventDefault();
      button.click();
    };
    const cancelMouseGesture = () => { mouseGestureRef.current = null; };
    window.addEventListener("mouseup", finishMouseGesture, true);
    window.addEventListener("blur", cancelMouseGesture);
    return () => {
      window.removeEventListener("mouseup", finishMouseGesture, true);
      window.removeEventListener("blur", cancelMouseGesture);
    };
  }, []);
  return (
    <div
      ref={markdownRootRef}
      className="markdown-answer"
      onClick={(event) => {
        const target = event.target as HTMLElement;
        const index = target.closest<HTMLElement>("[data-citation-index]")?.dataset.citationIndex;
        if (index) onCitation(Number(index), message);
      }}
    >
      <span ref={parkingRef} hidden aria-hidden="true" />
      {nodes.map((node) => renderMarkdownNode(node, citationHostsRef.current, parkingRef))}
      {citations.map((citation) => createPortal(
        <StableCitationButton citation={citation} onMouseDown={beginMouseGesture} />,
        citationHostsRef.current.get(citation.key)!,
        citation.key
      ))}
    </div>
  );
}
